/**
 * @file features/view/types
 * @description Contains types used for view loading etc.
 */

// Imports
import { GridSize } from '@material-ui/core';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { ChartType } from 'chart.js';
import { MaterialTableProps } from 'material-table';
import { RefObject, MutableRefObject } from 'react';
import { TFormData, TFormValue } from '../form';
import { IAction, TMode, TRole } from '../global';
import { VIEW_LOADING, VIEW_LOADED, VIEW_RESET } from './actions';

/**
 * @type TComponentType
 * @enum
 * @description Enum type for different types of components that can be displayed - the Components directory should define each of these.
 */
export enum EComponentType {
    'ViewText' = 'View Text',
    'EditShortText' = 'Short Text Input',
    'EditMasked' = 'Masked Input',
    'EditLongText' = 'Long Text/Formatted Text Input',
    // 'JsonBuilder' = 'Hidden JSON object builder',
    'SimpleSelect' = 'Simple Select Menu',
    'Autocomplete' = 'Autocomplete/Record Picker: Single Selection',
    'AutocompleteMultiple' = 'Autocomplete: Many Selections',
    'AddressPicker' = 'Address Picker',
    'SwitchInput' = 'On/Off Switch',
    'DateTimePicker' = 'Date/Time Picker',
    'DurationPicker' = 'Duration Entry',
    'FileAttachment' = 'Download/Upload File',
    'DataGrid' = 'Data Grid',
    'Button' = 'Button',
    'RenderIcon' = 'Icon',
    'SelectIcon' = 'Icon Picker',
    // 'QuerySelector' = 'Query Picker',
    // 'ButtonSelector' = 'Button Picker',
    'DialogButton' = 'Popup Dialog Button',
    'Chart' = 'Chart/Graph',
    'LabelMaker' = 'Label & Barcode Generator',
}
export type TComponentType = keyof typeof EComponentType;

/**
 * @type TDataType
 * @enum
 * @description Data type displayed by a component.
 */
export enum EDataType {
    'string' = 'String',
    'code' = 'Code',
    'number' = 'Number',
    'currency' = 'Currency',
    'phone' = 'Phone No',
    'email' = 'Email Address',
    'boolean' = 'True/False',
    'date' = 'Date/Time',
    'file' = 'File Attachment',
    'uuid' = 'UUID',
    'macaddress' = 'MAC Address',
    'link' = 'Link Text',
    'duration' = 'Duration',
    'object' = 'JSON Object',
    'array' = 'Array',
}
export type TDataType = keyof typeof EDataType;

/**
 * @type TWidthValues
 * @description Supported MUI xs width values for label/content display. A zero labelWidth means we don't show the label.
 */
export type TWidthValues = {
    labelWidth: GridSize | 0,
    valueWidth: GridSize,
};

/**
 * @type TCodeLang
 * @enum
 * @description Supported code languages - used for display and edit components for code (including JSON and rich text/limited HTML).
 */
export enum ECodeLang {
    'json',
    'richtext',
    'pgsql'
}
export type TCodeLang = keyof typeof ECodeLang;

/**
 * @type TShowRule
 * @description Type for a rule for determining whether to show a component. These rules are stored in a component's metadata - they refer to the keys and values to be used for logical tests.
 */
export type TShowRule = {
    testKey: string;            // dataKey containing the value(s) to test
    showValues?: any | any[];   // Static value(s) that return true if matched
    showKey?: string;           // dataKey containing value(s) that return true if matched
    strictTest?: boolean;       // Used for checking array members - true means the arrays must be exact; false means "contains" returns true
}


/**
 * @type TComponent
 * @description Converts an interface to a type alias - basic component definition.
 */
export interface IComponent {
    id: string;                             // ID shown in HTML
    componentType?: TComponentType;          // The component type
    showLabel?: boolean;                     // Display the label?
    dataKey?: string;                        // Form dataKey that holds the data for this component
    label?: string;                         // Label to show on forms
    dataType?: TDataType;                   // Data type displayed by component
    widths?: TWidthValues;                  // Grid xs width values
    code?: TCodeLang;                       // If dataType is 'code', what language?
    hideable?: boolean;                     // Can this component be hidden if rules are applied?
    showRules?: TShowRule[];                // Rules for determining whether to show the component
    clearDataOnHide?: boolean;              // False by default. If true, Redux state is cleared when the component is hidden. Useful for conditional autocompletes, etc. where we want to reset the state when a section is hidden, etc.
    show?: boolean;                         // Show the component? Usually computed by calculating showRules results but can be set directly to override
    style?: any;                            // Custom CSS styles - set to any to avoid compatibility issues between libraries etc.
    className?: string;                     // CSS class names used by MUI
    staticValue?: any;                      // Static value to display - e.g. for instructions fields
    defaultValue?: any;                     // Default value to display if selectedValue is empty - also serves as the pattern used for auto-generated defaults like certain entity names etc
    defaultValueKeys?: string[];            // dataKeys to concatenate (space separated by default) to compute a defaultValue
    defaultValueSeparator?: string;         // Separator for computed defaultValue when defaultValueKeys are supplied (if something other than a space)
    forceDefaultValueStart?: boolean;        // Force the selected value in a field to match the start of a default value pattern? If so, then anything typed into a field gets tacked onto the end of the default value
    required?: boolean;                 // Required field?
    ref?: ((instance: any | null) => void) | RefObject<any> | null;     // So that our components can take refs
    alreadyMounted?: MutableRefObject<boolean>;     // Has this component been mounted before?
    thisTabOrStep?: number;              // Current tab or step - used for determining if destructors should run
    componentsPerRow?: number;          // Used for calculating grid sizes
}
export type TComponent = IComponent;

/**
 * @type TComponentCollection
 * @description A key-value pair of string keys and overridable components (can take a ref) as values.
 */
export type TComponentCollection = {
    [key: string]: OverridableComponent<any>
};

/**
 * @type TInputComponent
 * @description Extends the IComponent interface to create a type alias for a form input component - for input components, more props are supported and dataKey is mandatory.
 */
export interface IInputComponent extends IComponent {
    dataKey: string;
    disabled?: boolean;
    updateKeys?: string[];     // These get updated at the same time as dataKey gets updated
    barcodeInput?: boolean;             // Do we show the bar/QR code tooltip and icon?
}
export type TInputComponent = IInputComponent;

/**
 * @type TFormatChars
 * @description This type has string key-value pairs that are used to define Regex rules for masked input. E.g. {'H': '[0-9a-fA-F]'} means 9 represents any hexadecimal digit.
 */
export type TFormatChars = {
    [maskChar: string]: string;
};

/**
 * @type TNumberSeparator
 * @enum
 * @description Acceptable thousand and decimal separator characters.
 */
export enum ENumberSeparator {
    ',',
    '.',
    ' ',
}
export type TNumberSeparator = keyof typeof ENumberSeparator;

/**
 * @type TMaskedInputComponent
 * @description Extends the IInputComponent interface and creates a type alias for masked input components. Accepts a few more properties needed for masked input components.
 */
interface IMaskedInputComponent extends IInputComponent {
    refObject: RefObject<any> | MutableRefObject<any>;      // Masked input components need a ref
    dataType: TDataType;            // Masked input components must specify a data type
    format?: string;                // Format string to apply for simple masks
    formatChars?: TFormatChars;     // Custom format characters for non-numeric and complex masks
    maskChar?: string;              // Character to display where we have empty places in the mask; this is _ by default.
    prefix?: string;                // Prefix that must always be present in input (e.g. $ for currencies)
    thousandSeparator?: TNumberSeparator;     // Thousand separator for numbers
    decimalSeparator?: TNumberSeparator;        // Decimal separator for numbers
    decimalScale?: number;              // Decimal scale for numeric data types
    allowEmptyFormatting?: boolean;     // Are empty values allowed?
    maskPlaceholder?: string;       // If provided, this will be considered an empty value for the purposes of input validation. It should be something like "________-____-____-____-____________" for UUIDs - i.e. fixed chars and blanks. Used with required fields.
}
export type TMaskedInputComponent = IMaskedInputComponent;

/**
 * @type TLinkTarget
 * @enum
 * @description Valid link targets
 */
export enum ELinkTarget {
    '_self',
    '_blank',
    '_parent',
    '_top'
}
export type TLinkTarget = keyof typeof ELinkTarget;

/**
 * @type TLinkComponent
 * @description Extends the IComponent interface and creates a type alias for link components.
 */
interface ILinkComponent extends IComponent {
    dataKey: string;            // The dataKey holds the link text
    linkHrefKey: string;        // The linkHrefKey holds the HREF URL
    linkTarget?: TLinkTarget;
}
export type TLinkComponent = ILinkComponent;

/**
 * @type TDatePicker
 * @enum
 * @description Valid date picker types
 */
export enum EDatePicker {
    'date',
    'time',
    'datetime-local'
}
export type TDatePicker = keyof typeof EDatePicker;

/**
 * @type TDateComponent
 * @description Extends the IComponent interface and creates a type alias for date components.
 */
interface IDateComponent extends IComponent {
    dataKey: string;
    dateFormat?: string;            // Date format string as used by the DayJS library - e.g. 'DD/MM/YYYY hh:mm a', which is the default
    datePickerType?: TDatePicker;        // For date picker components, which variant are we using?
    updateKeys?: string[];      // Only for date pickers (i.e. input components)
    disabled?: boolean;     // Also only for input components
    defaultStartEnd?: boolean;      // If this is true, the default start value will be the time the time the page was loaded and the end time will be the time the component was mounted
}
export type TDateComponent = IDateComponent;

/**
 * @type TAutocompleteComponent
 * @description Extends the IInputComponent interface and creates a type alias for autocompletes and select menus.
 */
interface IAutocompleteComponent extends IInputComponent {
    options?: string[];
    optionsKey?: string;
    disableClearable?: boolean;
    freeSolo?: boolean;
    fetchQueries?: number[];
    multiple?: boolean;
    fetchingLock?: boolean;
    valueKey?: string;
}
export type TAutocompleteComponent = IAutocompleteComponent;

/**
 * @type TGridColour
 * @description Supported grid colours for colour-coding. Each item consists of a name, text colour (can be hex, RGBA or name) and background colour. The custom colour is applied if the rowStyleRule (see TDataGrid below) with the same index returns true. rowStyleRules work like TShowRules - they are evaluated against test keys and test values; if they return true, the colour is applied. Earlier rowStyleRules override later ones (i.e. the first colour that returns true is applied).
 */
export type TGridColour = {
    background?: string,
    foreground?: string,
};

/**
 * @type TDataGrid
 * @description Extends the IComponent interface and creates a type alias for the DataGrid component's props. Basically merges the IComponent interface with MaterialTableProps.
 */
export type TDataGrid = MaterialTableProps<TFormData> & TComponent & {
    dataKey: string, 
    title: string, 
    linkParams?: TBodyParamMapping[], 
    newDialog?: TFormDialog, 
    editDialog?: TFormDialog,
    deleteDialog?: TFormDialog,
    rowStyleRules?: TShowRule[],
    rowStyleColours?: TGridColour[],
    showViewAction?: boolean,
    showEditAction?: boolean,
    showNewAction?: boolean,
    showDeleteAction?: boolean,
    baseHref?: boolean,
};

/**
 * @type TFormDialog
 * @description Extends the IComponent interface and creates a type alias for edit form dialogs (e.g. for grids, inline edits).
 */
interface IFormDialog extends IComponent {
    dialogText: string;
    sections: TSection[];
    confirmQueries: number[];
    displayStateSetter?: () => void;
    display: boolean;
    omitDataKeys?: string[];
}
export type TFormDialog = IFormDialog;

/**
 * @type TChart
 * @description Extends the IComponent interface and creates a type alias for Charts.
 */
 interface IChart extends IComponent {
    chartType: ChartType;
    dataLabels?: string[];
    dataLabelKeys?: string[];
    borderColours?: string[];
    backgroundColours?: string[];
    hoverBackgroundColours?: string[];
    dataKey: string;
    valueKey: string;
    drilldownBaseHref?: string;
    drilldownMode?: TMode;
    drilldownUrlParam?: TBodyParamMapping;
}
export type TChart = IChart;

/**
 * @type TButtonPurpose
 * @enum
 * @description The purpose of a button.
 */
export enum EButtonPurpose {
    'submit',
    'delete',
    'reset',
    'navigation',
    'showdialog',
    'wizardStart',
    'wizardNext',
    'wizardBack',
    'wizardLast',
    'formDialogPopup',
    'fileSelect',
    'misc',
}
export type TButtonPurpose = keyof typeof EButtonPurpose;

/**
 * @type TButton
 * @description Type for a Button. Extends the IComponent interface.
 */
interface IButton extends IComponent {
    button_purpose?: TButtonPurpose;
    clickQueries?: number[],
    variant?: 'text' | 'outlined' | 'contained',
    text?: string,
    color?: 'inherit' | 'primary' | 'secondary' | 'default',
    iconColor?: string,
    mode?: TMode,
    availableModes?: TMode[],
    baseHref?: string,
    fullWidth?: boolean,
    bodyParams?: TBodyParamMapping[],
    iconName?: TIconName,
    disabled?: boolean,
    customAction?: Function,
    customActionParams?: any[],
    ref?: ((instance: HTMLButtonElement | null) => void) | RefObject<HTMLButtonElement> | null,
    errorIfNone?: boolean
};
export type TButton = IButton;

/**
 * @type TIconName
 * @enum
 * @description Possible names for dynamically-shown icons. These are used to select the icon image that is rendered.
 */
export enum EIconName {
    'HomeIcon' = 'Home',
    'DashBoardIcon' = 'Dashboard',
    'PeopleIcon' = 'People',
    'AccountTreeIcon' = 'Tree',
    'ExtensionIcon' = 'Plugin/Extension',
    'HelpIcon' = 'Help',
    'BuildIcon' = 'Wrench',
    'FlightTakeoffIcon' = 'Plane Takeoff',
    'FunctionIcon' = 'Function Symbol',
    'LocationIcon' = 'Location',
    'SaveIcon' = 'Save',
    'DeleteIcon' = 'Delete',
    'NavIcon' = 'Navigate',
    'BackIcon' = 'Go Back',
    'RefreshIcon' = 'Refresh',
    'RestoreIcon' = 'Restore',
    'MenuIcon' = 'Menu',
    'HistoryIcon' = 'History',
    'ChevronLeftIcon' = 'Chevron Left',
    'ChevronRightIcon' = 'Chevron Right',
    'ForwardIcon' = 'Go Forward',
    'FastForwardIcon' = 'Go To Start',
    'FastRewindIcon' = 'Go To End',
    'CloudUploadIcon' = 'Upload File',
    'FileIcon' = 'File Attachment',
    'DownloadIcon' = 'Download Icon',
    'SearchIcon' = 'Search Icon',
    'CancelIcon' = 'Cancel Icon'
}
export type TIconName = keyof typeof EIconName;

/**
 * @type TRenderIconComponent
 * @description Extends the IComponent interface to create a type alias for the props for the RenderIcon component.
 */
interface IRenderIconComponent extends IComponent {
    showName?: 'above' | 'below' | 'left' | 'right' | false;
    color?: 'primary' | 'secondary' | 'default' | 'inherit';
}
export type TRenderIconComponent = IRenderIconComponent;

/**
 * @type TBodyParamMapping
 * @description Used to map a dataKey to a URL parameter used in a button definition (where they differ).
 */
export type TBodyParamMapping = {
    name: string,
    urlKey: string
};

/**
 * @type TDialogButton
 * @description Props for the DialogButton component, which are a combination of the FormDialog's and Button's props. It also has an additional prop for specifying the form data keys to dispatch (if any) - these are dispatched from form data with 'dialog_' prepended, so query parameters should be named accordingly. This convention applies to data grids too and allows form dialog values to be isolated from the rest of the form. Although we might have multiple form dialogs on the same page, each clears/sets these values when hidden/shown, so they shouldn't affect each other.
 */
interface IDialogButton extends IComponent {
    dialogProps: TFormDialog,
    buttonProps: TButton,
    dispatchKeys: string[]
}
export type TDialogButton = IDialogButton;

/**
 * @type TFileAttachment
 * @description Props for the FileAttachment component. Extends TInputComponent and adds props for the Category ID (numeric), a category ID key (if we want to get the category ID from Form Data) and other options.
 */
interface IFileAttachment extends IInputComponent {
    categoryId: number;
    saveFileIdQuery: number;
    categoryIdKey?: string;
    recordTableName: string;
    recordIdKey?: string;
    uploadable?: boolean;
    uploadSizeLimit?: number;
    projectIdKey?: string;
}
export type TFileAttachment = IFileAttachment;

/**
 * @type TLabelMaker
 * @description Props for the Label Maker component. Extends the TInputComponent props. Uses keys as follows:
 * - dataKey: The dataKey where the component will be displayed and to which the Object URL will be dispatched when it's generated.
 * - valueKey: Value containing the label heading.
 * - updateKeys: In order - other keys used to generate the label. These should have descriptive names.
 * - barcodeKey (optional): Barcodes will be generated for the value at this key. Due to size constraints, we only have one barcodeKey per label.
 * - customStaticText (optional): Add custom static text to the label. Accepts an array of key-value pairs. This allows us to set static label headings and add data to labels without needing hidden text fields.
 */
 interface ILabelMaker extends IInputComponent {
    dataKey: string;
    valueKey: string;
    updateKeys: string[];
    barcodeKey?: string;
    customStaticText?: TFormValue[];
}
export type TLabelMaker = ILabelMaker;

/**
 * @type TComponentPropType
 * @description Composite props type for any supported component type
 */
export type TComponentPropType = TComponent & (TLinkComponent | TInputComponent | TMaskedInputComponent | TAutocompleteComponent | TDateComponent | TButton | TRenderIconComponent | TDataGrid | TDialogButton | TFileAttachment | TChart | TLabelMaker);


/**
 * @type TSection
 * @description Type for a section, which is a group of components with a heading.
 */
export type TSection = {
    heading: string,
    componentsPerRow: 0 | 1 | 2 | 3,
    components: TComponentPropType[],
    thisTabOrStep?: number,
    ref?: ((instance: any | null) => void) | RefObject<any> | null;
};

/**
 * @type TParamType
 * @description Used to define query parameters' data type, data key, whether they're required, whether they can be taken from the URL and whether they are included in form body data.
 */
export type TParamType = {
    key: string,
    type: TDataType,
    required: boolean,
    urlParam?: boolean,
    bodyData?: boolean,
    maskPlaceholder?: string,
    label?: string,
};

/**
 * @type TQueryType
 * @enum
 * @description What kind of query is this? Select, insert, etc:
 * - select: Single DB SELECT query - can contain joins but must be capable of execution in a single statement.
 * - insert: Single DB INSERT query - can relate to one or several records, but must be capable of execution in a single statement.
 * - update: Single DB UPDATE query - can relate to one or several records, but must be capable of execution in a single statement.
 * - delete: Single DB DELETE query - @todo We will add support for this after beta.
 * - transaction: A multi-step query where the result of one step is used in another and rollbacks are possible. @todo SUPPORT TO BE ADDED THIS SPRINT!

 */
export enum EQueryType {
    'select',
    'insert',
    'update',
    'delete',
    'transaction',
}
export type TQueryType = keyof typeof EQueryType;

/**
 * @type TQueryPurpose
 * @enum
 * @description Why are we running this query? Fetching initial data, updating records or fetching autocomplete options etc?
 * - Meta: Fetch data using a /run/metadata route on view load.
 * - DataLoad: Fetch data using the /run/db/runquery route on view load.
 * - AutocompleteOptions: Fetch autocomplete options. Run on view load and when parent options for a linked autocomplete change.
 * - OnLoadSaga: Run a custom saga (/run/:queryType/:queryName) on view load.
 * - TriggeredSaga: Run a custom saga (/run/:queryType/:queryName) at another time - e.g. when a button is pressed or an event is fired.
 * - EditRecord: Creates or edits using the /run/db/runquery route - should be linked to submit buttons.
 * - EditMeta: Edits metadata using a /run/metadata route - should be linked to submit buttons.
 * - DeleteRecord: Deletes a record using the /run/db/runquery route - should be linked to delete buttons. @todo We are adding Delete support after the beta.
 * - DeleteMeta: Deletes metadata using a /run/metadata route - should be linked to delete buttons. @todo We are adding Delete support after the beta.
 */
export enum EQueryPurpose {
    'Meta' = 'Fetch Metadata/queries/Etc',
    'DataLoad' = 'Fetch Data from Database',
    'AutocompleteOptions' = 'Fetch Autocomplete Options',
    'OnLoadSaga' = 'Run a Saga on page load',
    'TriggeredSaga' = 'Run a Saga on a trigger',
    'EditRecord' = 'Create/Modify record(s)',
    'EditMeta' = 'Create/modify Metadata/queries/etc',
    'DeleteRecord' = 'Delete record(s)',
    'DeleteMeta' = 'Delete Metadata/queries/etc',
}
export type TQueryPurpose = keyof typeof EQueryPurpose;

/**
 * @constant {[key in TQueryType]: RegExp} queryRegexes Regexes that define different types of stored SQL queries we support and how table/field names and parameter tokens look. These are used for validity-testing and also detecting SQL strings that should be rendered with syntax highlighting.
 * @TODO: WE won't support delete or transactions until v 1.0.0-stable, so those are commented out.
 */
export const queryRegexes: { [key in TQueryType]: RegExp } = {
	select: /^SELECT\s.*FROM\s.*(WHERE\s.*)*$/,
	insert: /^INSERT\sINTO.*$/,
	update: /^UPDATE\s.*SET\s.*\sWHERE\s.*$/,
	delete: /^DELETE\sFROM\s.*WHERE\s.*$/,
	transaction: /^$/
};

/**
 * @constant {RegExp} sqlNameRegex Regex for testing if an SQL table or field name is valid - it must start with a letter or underscore and can only contain letters, numbers and underscores.
 */
export const sqlNameRegex = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * @constant {RegExp} parameterTokenRegex Regex for finding parameter tokens (e.g. SQL query parameters) - these are $1, $2, etc.
 */
export const parameterTokenRegex = /\$[1-9][0-9]*/g;

/**
 * @type TQuery
 * @description Type for a query used by a View - this type is the reference to the query that we store in View Metadata.
 */
export type TQuery = {
    purpose: TQueryPurpose | null,
    query_type: TQueryType,
    query_name: string,
    parameters: TParamType[],
    dataKey: string,
    valueKey?: string,
    updateKeys?: string[],
    omitKeys?: string[]
};

/**
 * @type TStoredQuery
 * @description Extended TQuery containing the additional propeties that we might have in a back-end object, including the query_data strings.
 */
export type TStoredQuery = TQuery & {
    query_data: string | {type: TQueryType, query: string}[],
    permitted_roles: TRole[],
    parameters: TParamType[] | (TParamType[])[],
    PartitionKey?: string,
    RowKey?: string,
    [x: string]: any
};

/**
 * @type TTabOrStep
 * @description Type for a tab or wizard step, which is a collection of sections that appear on a tab or wizard step.
 */
export type TTabOrStep = {
    title: string,                      // Title shown on top of tab or wizard step
    validateParams?: TParamType[],      // Series of parameter definitions (see TQuery) that must be validated for the step not be in "error" (wizard only)
    errorText?: string,                 // Error text shown if the step is in error (wizard only)
    sections: TSection[],               // Series of sections to show
    ref?: ((instance: any | null) => void) | RefObject<any> | null,      // Ref to pass
};

/**
 * @type TViewMetadata
 * @description Type for View metadata.
 */
export type TViewMetadata = {
    categoryAndScreen: string,
    view: TMode,
    query: TQuery[],
    tabsOrSteps: TTabOrStep[],
    topButtons: TButton[],
    bottomButtons: TButton[],
    availableModes: TMode[],
    ref?: ((instance: any | null) => void) | RefObject<any> | null,
};

/**
 * @type TViewState
 * @description The Redux state type for the currently-loaded view.
 */
export type TViewState = {
    loading: boolean,
    ready: boolean,
    readyTime: Date | null,
    currentView: TViewMetadata | null,
};

/**
 * @type TViewAction
 * @description Extends the IAction interface and creates a type alias for View actions.
 */
interface IViewAction extends IAction {
    type: typeof VIEW_LOADING | typeof VIEW_LOADED | typeof VIEW_RESET;
    payload?: TViewMetadata;
}
export type TViewAction = IViewAction;